/**
 * Audio Embedding Comparison Engine
 * 
 * Production-grade phoneme comparison using MediaPipe Audio Embedder (YAMNet).
 * 
 * Why this approach:
 * - YAMNet is trained on 2M+ audio samples from AudioSet
 * - Neural embeddings capture semantic audio content, not low-level features
 * - Robust to noise, speaker variation, and recording quality
 * - Cosine similarity is simple and interpretable
 * 
 * Architecture:
 * 1. Precompute embeddings for reference phoneme audio files
 * 2. When user speaks, compute embedding of their audio
 * 3. Cosine similarity between user and reference embeddings
 * 4. Single threshold for pass/fail
 */

import { AudioEmbedder, FilesetResolver } from '@mediapipe/tasks-audio';

// =============================================================================
// Configuration
// =============================================================================

const CONFIG = {
  // MediaPipe model
  MODEL_URL: 'https://storage.googleapis.com/mediapipe-models/audio_embedder/yamnet/float32/1/yamnet.tflite',
  WASM_URL: 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-audio@0.10.22-rc.20250304/wasm',  // Pinned to installed version
  
  // Audio processing
  SAMPLE_RATE: 16000,
  
  // Similarity threshold - STRICT because YAMNet sees all speech as similar
  // YAMNet is a general audio classifier, not phoneme-specific
  // We need high threshold to distinguish phonemes
  SIMILARITY_THRESHOLD: 0.80,
  
  // Phoneme-specific adjustments (relative to base 0.80)
  THRESHOLD_ADJUSTMENTS: {
    // Vowels (colors) - distinctive sounds, keep stricter
    black: 0.00, red: 0.00, green: 0.00, blue: 0.00,
    pink: 0.00, yellow: 0.00, orange: 0.00, brown: 0.00,
    plum: 0.00, aqua: 0.00, lime: 0.00, wood: 0.00,
    // Plosive consonants - very short sounds, harder to match
    b: -0.15, p: -0.15, t: -0.15, d: -0.15, k: -0.15, g: -0.15, c: -0.15,
    // Fricatives - slightly easier
    s: -0.10, f: -0.10, v: -0.10, z: -0.10,
    // Other consonants
    m: -0.05, n: -0.05, l: -0.05, r: -0.05,
  } as Record<string, number>,
};

// =============================================================================
// Phoneme Reference Files
// =============================================================================

const PHONEME_FILES: Record<string, string> = {
  // Colors (vowel sounds)
  black: '/audio/Isolated Phoneme_ Black.m4a',
  red: '/audio/Isolated Phoneme_ Red.m4a',
  green: '/audio/Isolated Phoneme_ Green.m4a',
  blue: '/audio/Isolated Phoneme_ Blue.m4a',
  pink: '/audio/Isolated Phoneme_ Pink.m4a',
  yellow: '/audio/Isolated Phoneme_ Yellow.m4a',
  orange: '/audio/Isolated Phoneme_ Orange.m4a',
  brown: '/audio/Isolated Phoneme_ Brown.m4a',
  plum: '/audio/Isolated Phoneme_ Plum.m4a',
  aqua: '/audio/Isolated Phoneme_ Aqua.m4a',
  lime: '/audio/Isolated Phoneme_ Lime.m4a',
  wood: '/audio/Isolated Phoneme_ Wood.m4a',
  // All consonants (complete set)
  b: '/audio/Isolated Phoneme_ B.m4a',
  c: '/audio/Isolated Phoneme_ C.m4a',
  d: '/audio/Isolated Phoneme_ D.m4a',
  f: '/audio/Isolated Phoneme_ F.m4a',
  g: '/audio/Isolated Phoneme_ G.m4a',
  h: '/audio/Isolated Phoneme_ H.m4a',
  j: '/audio/Isolated Phoneme_ J.m4a',
  k: '/audio/Isolated Phoneme_ K.m4a',
  l: '/audio/Isolated Phoneme_ L.m4a',
  m: '/audio/Isolated Phoneme_ M.m4a',
  n: '/audio/Isolated Phoneme_ N.m4a',
  p: '/audio/Isolated Phoneme_ P.m4a',
  r: '/audio/Isolated Phoneme_ R.m4a',
  s: '/audio/Isolated Phoneme_ S.m4a',
  t: '/audio/Isolated Phoneme_ T.m4a',
  v: '/audio/Isolated Phoneme_ V.m4a',
  w: '/audio/Isolated Phoneme_ W.m4a',
  x: '/audio/Isolated Phoneme_ X.m4a',
  y: '/audio/Isolated Phoneme_ Y.m4a',
  z: '/audio/Isolated Phoneme_ Z.m4a',
  ck: '/audio/Isolated Phoneme_ CK.m4a',
};

// Phonemes without reference audio (use STT-only)
// Only Q is missing now
const TTS_ONLY_PHONEMES = new Set(['q']);

// =============================================================================
// Singleton State
// =============================================================================

let embedder: AudioEmbedder | null = null;
let embedderInitializing: Promise<AudioEmbedder> | null = null;
let audioContext: AudioContext | null = null;

// Cache for reference embeddings (precomputed)
const referenceEmbeddings = new Map<string, Float32Array>();

// =============================================================================
// Initialization
// =============================================================================

/**
 * Initialize the MediaPipe Audio Embedder (singleton)
 */
async function getEmbedder(): Promise<AudioEmbedder> {
  if (embedder) return embedder;
  
  if (embedderInitializing) {
    return embedderInitializing;
  }
  
  embedderInitializing = (async () => {
    try {
      console.log('[AudioEmbed] Initializing MediaPipe Audio Embedder...');
      console.log('[AudioEmbed] WASM URL:', CONFIG.WASM_URL);
      console.log('[AudioEmbed] Model URL:', CONFIG.MODEL_URL);
      
      const audioFileset = await FilesetResolver.forAudioTasks(CONFIG.WASM_URL);
      
      embedder = await AudioEmbedder.createFromOptions(audioFileset, {
        baseOptions: {
          modelAssetPath: CONFIG.MODEL_URL,
        },
      });
      
      console.log('[AudioEmbed] ✓ MediaPipe Audio Embedder ready');
      return embedder;
    } catch (err) {
      console.error('[AudioEmbed] Failed to initialize MediaPipe:', err);
      embedderInitializing = null; // Allow retry
      throw err;
    }
  })();
  
  return embedderInitializing;
}

/**
 * Get audio context for decoding
 */
function getAudioContext(): AudioContext {
  if (!audioContext || audioContext.state === 'closed') {
    audioContext = new AudioContext({ sampleRate: CONFIG.SAMPLE_RATE });
  }
  return audioContext;
}

// =============================================================================
// Audio Processing
// =============================================================================

/**
 * Decode audio blob to Float32Array samples
 */
async function decodeAudio(blob: Blob): Promise<Float32Array> {
  const ctx = getAudioContext();
  const arrayBuffer = await blob.arrayBuffer();
  const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
  
  // Convert to mono if stereo
  if (audioBuffer.numberOfChannels === 1) {
    return audioBuffer.getChannelData(0);
  }
  
  const left = audioBuffer.getChannelData(0);
  const right = audioBuffer.getChannelData(1);
  const mono = new Float32Array(left.length);
  
  for (let i = 0; i < left.length; i++) {
    mono[i] = (left[i] + right[i]) / 2;
  }
  
  return mono;
}

/**
 * Load audio file and decode to samples
 */
async function loadAudioFile(url: string): Promise<Float32Array> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load audio: ${url}`);
  }
  
  const blob = await response.blob();
  return decodeAudio(blob);
}

/**
 * Extract speech from audio using simple energy-based VAD
 * Returns the loudest 1-second segment
 */
function extractSpeechSegment(samples: Float32Array): Float32Array {
  const frameSize = Math.floor(CONFIG.SAMPLE_RATE * 0.025); // 25ms
  const hopSize = Math.floor(CONFIG.SAMPLE_RATE * 0.010);   // 10ms
  
  // Find frame with maximum energy
  let maxEnergy = 0;
  let maxFrame = 0;
  
  for (let i = 0; i < samples.length - frameSize; i += hopSize) {
    let energy = 0;
    for (let j = 0; j < frameSize; j++) {
      energy += samples[i + j] * samples[i + j];
    }
    if (energy > maxEnergy) {
      maxEnergy = energy;
      maxFrame = i;
    }
  }
  
  // Extract 1 second centered on peak (or less if audio is shorter)
  const targetLength = Math.min(samples.length, CONFIG.SAMPLE_RATE);
  const halfLength = Math.floor(targetLength / 2);
  const start = Math.max(0, Math.min(maxFrame - halfLength, samples.length - targetLength));
  const end = Math.min(samples.length, start + targetLength);
  
  return samples.slice(start, end);
}

// =============================================================================
// Embedding Extraction
// =============================================================================

/**
 * Get embedding for audio samples
 */
async function getEmbedding(samples: Float32Array): Promise<Float32Array> {
  const embedderInstance = await getEmbedder();
  
  // MediaPipe embed() returns array of AudioEmbedderResult (one per timestamp)
  const results = embedderInstance.embed(samples);
  
  if (!results || results.length === 0) {
    throw new Error('No results returned from MediaPipe');
  }
  
  // Collect all embeddings from all timestamp results
  const allEmbeddings: Float32Array[] = [];
  
  for (const result of results) {
    if (result.embeddings && result.embeddings.length > 0) {
      for (const emb of result.embeddings) {
        if (emb.floatEmbedding) {
          allEmbeddings.push(new Float32Array(emb.floatEmbedding));
        }
      }
    }
  }
  
  if (allEmbeddings.length === 0) {
    throw new Error('No embeddings found in MediaPipe results');
  }
  
  // Return single embedding or average multiple
  if (allEmbeddings.length === 1) {
    return allEmbeddings[0];
  }
  
  // Average multiple embeddings
  const dim = allEmbeddings[0].length;
  const averaged = new Float32Array(dim);
  
  for (const emb of allEmbeddings) {
    for (let i = 0; i < dim; i++) {
      averaged[i] += emb[i];
    }
  }
  
  for (let i = 0; i < dim; i++) {
    averaged[i] /= allEmbeddings.length;
  }
  
  return averaged;
}

/**
 * Compute cosine similarity between two embeddings
 */
function cosineSimilarity(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length) {
    throw new Error('Embedding dimension mismatch');
  }
  
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;
  
  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  
  const denominator = Math.sqrt(normA) * Math.sqrt(normB);
  if (denominator === 0) return 0;
  
  return dotProduct / denominator;
}

// =============================================================================
// Reference Embedding Management
// =============================================================================

/**
 * Precompute embedding for a reference phoneme
 */
async function precomputeReferenceEmbedding(phoneme: string): Promise<Float32Array | undefined> {
  const key = phoneme.toLowerCase();

  // Check cache
  if (referenceEmbeddings.has(key)) {
    return referenceEmbeddings.get(key)!;
  }

  // Check if phoneme has reference audio
  const url = PHONEME_FILES[key];
  if (!url) {
    console.log(`[AudioEmbed] No reference for: ${phoneme} (TTS-only)`);
    return undefined;
  }

  try {
    console.log(`[AudioEmbed] Loading reference: ${phoneme}`);
    const samples = await loadAudioFile(url);
    const embedding = await getEmbedding(samples);

    referenceEmbeddings.set(key, embedding);
    console.log(`[AudioEmbed] ✓ Cached reference: ${phoneme} (${embedding.length}d)`);

    return embedding;
  } catch (err) {
    console.error(`[AudioEmbed] Failed to load reference: ${phoneme}`, err);
    return undefined;
  }
}

/**
 * Preload all reference embeddings
 */
export async function preloadAllReferences(): Promise<void> {
  console.log('[AudioEmbed] Preloading all reference embeddings...');
  
  // Initialize embedder first
  await getEmbedder();
  
  // Load all phonemes in parallel (but not too many at once)
  const phonemes = Object.keys(PHONEME_FILES);
  const batchSize = 4;
  
  for (let i = 0; i < phonemes.length; i += batchSize) {
    const batch = phonemes.slice(i, i + batchSize);
    await Promise.all(batch.map(p => precomputeReferenceEmbedding(p)));
  }
  
  console.log(`[AudioEmbed] ✓ Preloaded ${referenceEmbeddings.size} reference embeddings`);
}

/**
 * Preload specific phonemes
 */
export async function preloadReferences(phonemes: string[]): Promise<void> {
  try {
    await getEmbedder();
    
    for (const phoneme of phonemes) {
      await precomputeReferenceEmbedding(phoneme);
    }
  } catch (err) {
    console.error('[AudioEmbed] Failed to preload references:', err);
    // Don't throw - allow app to continue, will fall back to STT-only
  }
}

// =============================================================================
// Comparison API
// =============================================================================

export interface PhonemeComparisonResult {
  isCorrect: boolean;
  similarity: number;
  threshold: number;
  phoneme: string;
  hasReference: boolean;
  feedback: string;
}

/**
 * Compare user audio to reference phoneme
 */
export async function comparePhoneme(
  userAudio: Blob,
  phoneme: string
): Promise<PhonemeComparisonResult> {
  const key = phoneme.toLowerCase();
  
  // Check if this phoneme has reference audio
  if (TTS_ONLY_PHONEMES.has(key) || !PHONEME_FILES[key]) {
    return {
      isCorrect: false,
      similarity: -1,
      threshold: 0,
      phoneme,
      hasReference: false,
      feedback: 'No reference audio (STT-only verification)',
    };
  }
  
  try {
    // Check if embedder is available
    try {
      await getEmbedder();
    } catch {
      console.warn('[AudioEmbed] Embedder not available, falling back to STT-only');
      return {
        isCorrect: false,
        similarity: -1,
        threshold: 0,
        phoneme,
        hasReference: false,
        feedback: 'Embedding model not available (STT-only verification)',
      };
    }
    
    // Get reference embedding (from cache or compute)
    let refEmbedding = referenceEmbeddings.get(key);
    if (!refEmbedding) {
      refEmbedding = await precomputeReferenceEmbedding(key);
      if (!refEmbedding) {
        return {
          isCorrect: false,
          similarity: -1,
          threshold: 0,
          phoneme,
          hasReference: false,
          feedback: 'Failed to load reference audio',
        };
      }
    }
    
    // Decode user audio
    const userSamples = await decodeAudio(userAudio);
    
    // Extract speech segment (focus on loudest part)
    const speechSegment = extractSpeechSegment(userSamples);
    
    console.log(`[AudioEmbed] User audio: ${(userSamples.length / CONFIG.SAMPLE_RATE).toFixed(2)}s → ${(speechSegment.length / CONFIG.SAMPLE_RATE).toFixed(2)}s`);
    
    // Get user embedding
    const userEmbedding = await getEmbedding(speechSegment);
    
    // Compute cosine similarity
    const similarity = cosineSimilarity(userEmbedding, refEmbedding);
    
    // Apply phoneme-specific threshold adjustment
    const adjustment = CONFIG.THRESHOLD_ADJUSTMENTS[key] || 0;
    const threshold = CONFIG.SIMILARITY_THRESHOLD + adjustment;
    
    const isCorrect = similarity >= threshold;
    
    console.log(`[AudioEmbed] ${phoneme}: similarity=${similarity.toFixed(3)}, threshold=${threshold.toFixed(2)}, ${isCorrect ? '✓ PASS' : '✗ FAIL'}`);
    
    return {
      isCorrect,
      similarity,
      threshold,
      phoneme,
      hasReference: true,
      feedback: isCorrect ? 'Great job!' : 'Try again!',
    };
  } catch (err) {
    console.error(`[AudioEmbed] Comparison error:`, err);
    return {
      isCorrect: false,
      similarity: 0,
      threshold: CONFIG.SIMILARITY_THRESHOLD,
      phoneme,
      hasReference: true,
      feedback: 'Error processing audio',
    };
  }
}

// =============================================================================
// Exports
// =============================================================================

export {
  CONFIG as EMBEDDING_CONFIG,
  TTS_ONLY_PHONEMES,
};

/**
 * Check if phoneme has reference audio for embedding comparison
 */
export function hasReferenceAudio(phoneme: string): boolean {
  const key = phoneme.toLowerCase();
  return !TTS_ONLY_PHONEMES.has(key) && !!PHONEME_FILES[key];
}

/**
 * Set global similarity threshold
 */
export function setSimilarityThreshold(threshold: number): void {
  CONFIG.SIMILARITY_THRESHOLD = threshold;
  console.log(`[AudioEmbed] Threshold set to: ${threshold}`);
}

/**
 * Get current threshold for a phoneme
 */
export function getThreshold(phoneme: string): number {
  const adjustment = CONFIG.THRESHOLD_ADJUSTMENTS[phoneme.toLowerCase()] || 0;
  return CONFIG.SIMILARITY_THRESHOLD + adjustment;
}
